import { useEffect } from 'react'

// =====================================================
// Component: UpdatePrompt
// Purpose: Bring in a new version of the site, but never in the middle of a lesson
// =====================================================
//
// The site checks for a new version every 30 seconds. It used to switch to it at once and reload every open tab, so a
// deploy during class reloaded the projector, the control laptop and every student's game (Astra's review, Nov 2
// 2026). Now a new version waits (the service worker is built with registerType 'prompt': it doesn't take over by
// itself) until no tab of the site on this device is busy, and then comes in quietly:
// - busy: a page under /play/ (a class game, the projector and control laptop, the teacher's pages there, the slime
//   room and its games, the creator) or a practice task under /practice/. Anything else (the board, materials, the
//   game and practice menus) can reload without losing anything.
// - the tabs ask each other over a BroadcastChannel before the switch, since the switch reaches every tab of the site.
// - a busy tab that the new version reached anyway (a tab that became busy in the moment between asking and the
//   switch) keeps running what it has, and reloads as soon as it isn't busy any more.
// No sign is shown: it happens between lessons, without anyone needing to do anything.

const CHECK_MS = 30_000 // how often to look for a new version
const LOOK_MS = 5_000 // how often a tab looks at whether it has stopped being busy
const ASK_MS = 400 // how long to wait for another tab to say it's busy

export const busyPath = (path: string) => /^\/play\/./.test(path) || /^\/practice\/./.test(path)
const busy = () => busyPath(location.pathname)

export function UpdatePrompt() {
  useEffect(() => {
    if (!import.meta.env.PROD || !('serviceWorker' in navigator)) return
    const sw = navigator.serviceWorker
    const channel = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel('gk-update') : null
    let reg: ServiceWorkerRegistration | undefined
    let switching = false // this tab asked for the new version to take over
    let behind = false // the new version took over while this tab was busy: reload once it isn't
    let stopped = false

    // another tab asking whether anyone is busy
    if (channel)
      channel.onmessage = (e) => {
        if (e.data?.t === 'busy?' && busy()) channel.postMessage({ t: 'busy', id: e.data.id })
      }

    const anyoneBusy = () =>
      new Promise<boolean>((done) => {
        if (busy()) return done(true)
        if (!channel) return done(false)
        const id = Math.random().toString(36).slice(2)
        const asker = new BroadcastChannel('gk-update')
        const t = setTimeout(() => (asker.close(), done(false)), ASK_MS)
        asker.onmessage = (e) => {
          if (e.data?.t === 'busy' && e.data.id === id) {
            clearTimeout(t)
            asker.close()
            done(true)
          }
        }
        asker.postMessage({ t: 'busy?', id })
      })

    // a new version is waiting: bring it in if nobody on this device is in the middle of something
    const tryNow = async () => {
      if (stopped || switching || !reg?.waiting || !sw.controller) return
      if (await anyoneBusy()) return
      const waiting = reg.waiting
      if (!waiting || stopped) return
      switching = true
      waiting.postMessage({ type: 'SKIP_WAITING' })
    }

    // the new version has taken over this tab
    const onTakeOver = () => {
      if (switching || !busy()) location.reload()
      else behind = true
    }
    sw.addEventListener('controllerchange', onTakeOver)

    const look = setInterval(() => {
      if (behind && !busy()) location.reload()
      else void tryNow()
    }, LOOK_MS)

    let check: ReturnType<typeof setInterval> | undefined
    sw.register('/sw.js', { scope: '/' })
      .then((r) => {
        reg = r
        // a version that finishes installing while this tab is open
        r.addEventListener('updatefound', () => {
          const next = r.installing
          next?.addEventListener('statechange', () => {
            if (next.state === 'installed') void tryNow()
          })
        })
        void tryNow()
        check = setInterval(() => void r.update().catch(() => {}), CHECK_MS)
      })
      .catch((error) => console.error('SW registration error', error))

    return () => {
      stopped = true
      clearInterval(look)
      clearInterval(check)
      sw.removeEventListener('controllerchange', onTakeOver)
      channel?.close()
    }
  }, [])

  return null
}
